import Button from 'components/Button';
import FloatingButton from 'components/FloatingButton';
import TagsFilter from 'components/Forms/lib/TagsFilter';
import PremiumIcon from 'components/PremiumIcon';
import SelectFilters from 'components/SelectFilters';
import Title from 'components/Typography/Title';
import ApplicationStatus from 'core/enums/applicationStatus';
import Application from 'core/models/application';
import Tag from 'core/models/tag';
import { useApplicationsQuery } from 'core/api/applications';
import { getApplicationDateOfPublication, getApplicationSource } from 'core/useCases/applications/utils';
import { ModalType } from 'core/useCases/modal/types';
import { useModalStore } from 'stores/modal';
import useResponsive from 'hooks/responsive';
import useScrollMargin from 'hooks/scrollMargin';
import { useIsPremium } from 'hooks/userIsPremium';
import Checked from 'images/icons/Checked.svg?react';
import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import { STable } from 'styles/components';
import { SEndRow } from 'styles/layout';
import ApplicationActions from '../ApplicationActions';
import ApplicationStatusComponent from '../ApplicationStatus';
import ApplicationSubjectItem from '../ApplicationSubjectItem';
import MarkApplicationAsCompleteButton from '../MarkApplicationAsCompleteButton';
import {
    SCard,
    SCardHeader,
    SCardHeaderLeft,
    SCardHeaderStatus,
    SCardLine,
    SCardLineLeft,
    SCardLineRight,
    SColumnResponsive,
    SImportButton,
    SPremiumButtonDiv,
    SPremiumDiv,
    SRight,
    SRightResponsive,
    SScrollContainer,
    SSearchInput,
    SSpacedRowResponsive,
    SWrapper,
} from './styles';

type ApplicationListProps = {
    applicationsToDisplay?: Application[];
    truncate?: boolean;
};

const ApplicationList: FC<ApplicationListProps> = ({ applicationsToDisplay, truncate = false }) => {
    const { t } = useTranslation();
    const openModal = useModalStore((s) => s.openModal);
    // Mémoïsé : éviter un nouveau tableau à chaque render (sinon le useEffect de
    // filtrage boucle via setState pendant le chargement de la query).
    const { data: applicationsData } = useApplicationsQuery();
    const applications = useMemo(
        () => applicationsToDisplay || applicationsData || [],
        [applicationsToDisplay, applicationsData],
    );
    const { isMobile } = useResponsive();
    const [search, setSearch] = useState<string>('');
    const [filteredApplication, setFilteredApplication] = useState<Application[]>([]);
    const [filterOptionsValues, setFilterOptionsValues] = useState<string[]>([]);
    const history = useHistory();
    const defaultTag: Tag | undefined = history.location.state?.tag;
    const [tagsFilters, setTagsFilters] = useState<Tag[]>(defaultTag ? [defaultTag] : []);
    const isPremium = useIsPremium();

    // Scroll géré par la fenêtre : la barre de scroll reste sur les bords du
    // navigateur. scrollMargin = offset de la liste dans le document, requis par
    // useWindowVirtualizer pour positionner correctement les éléments.
    const { ref: scrollRef, scrollMargin } = useScrollMargin<HTMLDivElement>();

    const virtualizer = useWindowVirtualizer({
        count: filteredApplication.length,
        estimateSize: () => (isMobile ? 320 : 56),
        overscan: 10,
        scrollMargin,
    });

    // estimateSize dépend de isMobile : au changement de breakpoint, on recalcule
    // les tailles sinon le cache du virtualizer garde les hauteurs de l'autre layout.
    useEffect(() => {
        virtualizer.measure();
    }, [isMobile, virtualizer]);

    const virtualItems = virtualizer.getVirtualItems();
    const totalSize = virtualizer.getTotalSize();
    const paddingTop = virtualItems.length > 0 ? virtualItems[0].start - scrollMargin : 0;
    const paddingBottom =
        virtualItems.length > 0 ? totalSize - (virtualItems[virtualItems.length - 1].end - scrollMargin) : 0;
    const columnCount = truncate ? 6 : 9;

    const filterOptions = [
        {
            component: <ApplicationStatusComponent status={ApplicationStatus.Engaged} />,
            value: ApplicationStatus.Engaged.toString(),
        },
        {
            component: <ApplicationStatusComponent status={ApplicationStatus.Lost} />,
            value: ApplicationStatus.Lost.toString(),
        },
        {
            component: <ApplicationStatusComponent status={ApplicationStatus.New} />,
            value: ApplicationStatus.New.toString(),
        },
        {
            component: <ApplicationStatusComponent status={ApplicationStatus.Win} />,
            value: ApplicationStatus.Win.toString(),
        },
    ];

    const openApplicationModal = () => {
        openModal({
            title: t('application.creation.label'),
            type: ModalType.Application,
        });
    };

    const redirectToAskSubscription = () => {
        if (isPremium === false) {
            history.push('/ask-subscription');
        }
    };

    const openAiImportModal = () => {
        openModal({
            title: t('prompt-generator-opportunity.modal-title'),
            type: ModalType.PromptGeneratorOpportunity,
        });
    };

    function sort(applicationsToSort: Application[]): Application[] {
        return applicationsToSort.sort(
            (applicationA: Application, applicationB: Application) =>
                new Date(applicationB.createdDate).getTime() - new Date(applicationA.createdDate).getTime(),
        );
    }

    useEffect(() => {
        let localApplications = [...applications];

        if (filterOptionsValues.length > 0) {
            localApplications = localApplications.filter((application) =>
                filterOptionsValues.includes(application.status),
            );
        }

        if (tagsFilters.length > 0) {
            localApplications = localApplications.filter((application) =>
                tagsFilters.some((tag) => application.tags?.some((appTag: Tag) => appTag.name === tag.name)),
            );
        }

        if (search) {
            const computedSearch = search.toLowerCase();
            localApplications = localApplications.filter((application) =>
                application.company.toLowerCase().includes(computedSearch),
            );
        }

        setFilteredApplication(sort(localApplications));
    }, [applications, search, filterOptionsValues, tagsFilters]);

    const handleSearchClick = () => {
        if (!isPremium) {
            history.push('/ask-subscription');
        }
    };

    const handleTagsFilterClick = () => {
        if (!isPremium) {
            history.push('/ask-subscription');
        }
    };

    const SearchInput = (
        <SSearchInput>
            <input
                onChange={(event) => setSearch(event.target.value)}
                onClick={handleSearchClick}
                placeholder={t('application.search-for-a-application')}
                type="text"
                value={search}
            />
            <SPremiumDiv>
                <PremiumIcon />
            </SPremiumDiv>
        </SSearchInput>
    );

    const AiImportButton = (
        <SImportButton onClick={!isPremium ? redirectToAskSubscription : openAiImportModal} withShadow>
            <SPremiumButtonDiv>
                <PremiumIcon />
            </SPremiumButtonDiv>
            {t('prompt-generator-opportunity.button')}
        </SImportButton>
    );

    const Actions = () => (
        <>
            {AiImportButton}
            <Button onClick={openApplicationModal} withShadow>
                {t('application.creation.label')}
            </Button>
        </>
    );

    return (
        <SWrapper>
            {!truncate && (
                <SSpacedRowResponsive>
                    <SColumnResponsive>
                        <Title text={t('page.application.title')} />
                        <span>
                            {applications.length} {t('common.applications')}
                        </span>
                    </SColumnResponsive>
                    <SRightResponsive>
                        <TagsFilter
                            isPremium={isPremium}
                            onChange={setTagsFilters}
                            onClickToPremiumField={handleTagsFilterClick}
                            tagFilters={tagsFilters}
                            isPremiumFeature
                        />
                        <SRight>
                            {SearchInput}
                            <SelectFilters
                                isPremium={isPremium}
                                options={filterOptions}
                                selectedOptionsValues={filterOptionsValues}
                                setSelectedOptionsValues={setFilterOptionsValues}
                                isPremiumFeature
                            />
                        </SRight>
                    </SRightResponsive>
                </SSpacedRowResponsive>
            )}
            {isMobile ? (
                <SScrollContainer ref={scrollRef}>
                    {paddingTop > 0 && <div style={{ height: paddingTop }} />}
                    {virtualItems.map((virtualRow) => {
                        const index = virtualRow.index;
                        const application = filteredApplication[index];
                        const { id, status, sourceDetails, reference, place, salary } = application;

                        return (
                            <SCard
                                key={id}
                                ref={virtualizer.measureElement}
                                data-index={index}
                                isEven={index % 2 === 0}
                            >
                                <SCardHeader>
                                    <SCardHeaderLeft>
                                        {![ApplicationStatus.Win, ApplicationStatus.Lost].includes(status) ? (
                                            <MarkApplicationAsCompleteButton application={application} />
                                        ) : (
                                            <Checked />
                                        )}
                                        <SCardHeaderStatus>
                                            <ApplicationSubjectItem
                                                application={application}
                                                isCompleted={[ApplicationStatus.Win, ApplicationStatus.Lost].includes(
                                                    status,
                                                )}
                                                isJobShown={false}
                                            />
                                            <ApplicationStatusComponent status={status} />
                                        </SCardHeaderStatus>
                                    </SCardHeaderLeft>
                                    <ApplicationActions applicationId={id} />
                                </SCardHeader>
                                <SCardLine>
                                    <SCardLineLeft>{t('common.job.label')}</SCardLineLeft>
                                    <SCardLineRight>{application.job}</SCardLineRight>
                                </SCardLine>
                                <SCardLine>
                                    <SCardLineLeft>{t('application.reference.label')}</SCardLineLeft>
                                    <SCardLineRight>{reference}</SCardLineRight>
                                </SCardLine>
                                <SCardLine>
                                    <SCardLineLeft>{t('application.source.label')}</SCardLineLeft>
                                    <SCardLineRight>{getApplicationSource(application, t)}</SCardLineRight>
                                </SCardLine>
                                <SCardLine>
                                    <SCardLineLeft>{t('application.source-details.label')}</SCardLineLeft>
                                    <SCardLineRight>{sourceDetails}</SCardLineRight>
                                </SCardLine>
                                <SCardLine>
                                    <SCardLineLeft>{t('application.salary.label')}</SCardLineLeft>
                                    <SCardLineRight>{salary}</SCardLineRight>
                                </SCardLine>
                                <SCardLine>
                                    <SCardLineLeft>{t('application.date-of-publication')}</SCardLineLeft>
                                    <SCardLineRight>{getApplicationDateOfPublication(application)}</SCardLineRight>
                                </SCardLine>
                                <SCardLine>
                                    <SCardLineLeft>{t('application.place.label')}</SCardLineLeft>
                                    <SCardLineRight>{place}</SCardLineRight>
                                </SCardLine>
                            </SCard>
                        );
                    })}
                    {paddingBottom > 0 && <div style={{ height: paddingBottom }} />}
                </SScrollContainer>
            ) : (
                <SScrollContainer ref={scrollRef}>
                    <STable>
                        <thead>
                            <tr>
                                <th aria-label={t('common.action')} />
                                <th>{t('common.subject.label')}</th>
                                <th>{t('application.reference.label')}</th>
                                <th>{t('application.salary.label')}</th>
                                {!truncate && (
                                    <>
                                        <th>{t('application.source.label')}</th>
                                        <th>{t('application.source-details.label')}</th>
                                        <th>{t('application.date-of-publication')}</th>
                                    </>
                                )}
                                <th>{t('application.place.label')}</th>
                                <th>{t('common.status')}</th>
                                <th aria-label={t('common.action')} />
                            </tr>
                        </thead>
                        <tbody className="table--application">
                            {paddingTop > 0 && (
                                <tr>
                                    <td colSpan={columnCount} style={{ height: paddingTop, padding: 0 }} />
                                </tr>
                            )}
                            {virtualItems.map((virtualRow) => {
                                const application = filteredApplication[virtualRow.index];
                                const { id, status, sourceDetails, reference, place, salary } = application;

                                return (
                                    <tr key={id} ref={virtualizer.measureElement} data-index={virtualRow.index}>
                                        <td>
                                            {![ApplicationStatus.Win, ApplicationStatus.Lost].includes(status) ? (
                                                <MarkApplicationAsCompleteButton application={application} />
                                            ) : (
                                                <Checked />
                                            )}
                                        </td>
                                        <td>
                                            <ApplicationSubjectItem
                                                application={application}
                                                isCompleted={[ApplicationStatus.Win, ApplicationStatus.Lost].includes(
                                                    status,
                                                )}
                                            />
                                        </td>
                                        <td>{reference}</td>
                                        <td>{salary}</td>
                                        {!truncate && (
                                            <>
                                                <td>{getApplicationSource(application, t)}</td>
                                                <td>{sourceDetails}</td>
                                                <td>{getApplicationDateOfPublication(application)}</td>
                                            </>
                                        )}
                                        <td>{place}</td>
                                        <td>
                                            <ApplicationStatusComponent status={status} />
                                        </td>
                                        <td>
                                            <div className="showOnRowHover">
                                                <ApplicationActions applicationId={id} />
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                            {paddingBottom > 0 && (
                                <tr>
                                    <td colSpan={columnCount} style={{ height: paddingBottom, padding: 0 }} />
                                </tr>
                            )}
                        </tbody>
                    </STable>
                </SScrollContainer>
            )}
            {!truncate && applications.length > 0 ? (
                <FloatingButton>
                    <Actions />
                </FloatingButton>
            ) : (
                <SEndRow>
                    <Actions />
                </SEndRow>
            )}
        </SWrapper>
    );
};

export default ApplicationList;
